import { UseControllerReturn } from 'react-hook-form';
import { useCallback, KeyboardEvent, ChangeEvent } from 'react';

import { thousandSeparator, removeNonNumber } from '@utils/number';

import { getNewCursorPosition } from '../utils';

interface UseCurrencyInputHandleOptions {
  controllerOnChange: UseControllerReturn['field']['onChange'];
  controllerValue: unknown;
  isAllowOnlyZero: boolean;
}

function useCurrencyInputHandler({
  controllerOnChange,
  isAllowOnlyZero,
}: UseCurrencyInputHandleOptions) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const target = e.target as EventTarget & HTMLInputElement;

      if (
        target.value.length > 1 &&
        target.selectionStart === 0 &&
        e.key === '0' &&
        !isAllowOnlyZero
      ) {
        return e.preventDefault();
      }
    },
    [isAllowOnlyZero],
  );

  const handleOnChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const target = e.target;
      if (!/^[0-9.]*$/.test(target.value)) {
        return (target.value = '');
      }

      if (target.value.charAt(0) === '0' && (target.selectionStart ?? 0) > 0) {
        if (!isAllowOnlyZero) return (target.value = '');

        target.value = '0';
        controllerOnChange(0);
      } else {
        const value = thousandSeparator(target.value, {
          isReturnEmptyStringOnEmptyValue: true,
        });
        const newCursorPosition = getNewCursorPosition({
          beforeValue: target.value,
          afterValue: value,
          cursorPosition: target.selectionStart ?? 0,
        });
        target.value = value;
        target.selectionStart = newCursorPosition;
        target.selectionEnd = newCursorPosition;
        target.setAttribute('plain-value', removeNonNumber(value));
        controllerOnChange(removeNonNumber(value));
      }
    },
    [controllerOnChange, isAllowOnlyZero],
  );

  return { handleKeyDown, handleOnChange };
}

export default useCurrencyInputHandler;
